import React, {useEffect, useMemo, useState} from 'react';
import {FieldInputProps} from 'formik';
import {Box, Button, Grid, Typography} from '@mui/material';
import {useQuery} from '@apollo/client';
import {toString, merge} from 'lodash-es';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {
  criticalIllnessCoverField,
  deferredPeriodField,
  protectionInTrustField,
  investmentLinkedField,
  liveCoverField,
  livesAssuredField,
  monthlyIncomeTextField,
  premiumTypeField,
  protectionFields,
  protectionPlanNotesField,
  protectionTypeField,
  protectionTypeToFieldsMatrix,
  setUpProtectionFields,
  termField,
  termTypeField,
  toAgeField,
  waiverOfPremiumField,
} from './builderProtectionFields';
import AutoField from '../../../components/AutoForm/AutoFieldNew';
import {ProtectionRow} from './ProtectionRow';
import OWNERSHIP from '../../../constants/Ownership';
import {BuilderProps} from '../CreateBuilder/BuilderSourceDestination/builderValidators';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {OV_PROTECTION_BUILDER_DETAILS} from '../BuilderGrid/builderOvPotQueries';
import {
  OvProtectionbuilderDetails,
  OvProtectionbuilderDetailsVariables,
  OvProtectionbuilderDetails_ovrecommendDetails as OvProtectionbuilderDetails_ovbuilderDetails,
} from '../../../schema-types/OvProtectionbuilderDetails';
import {ProtectionTypeEnum} from '../../../schema-types/globalTypes';
import Loading from '../../../components/Loading/Loading';
import {
  useAutoForm,
  useAutoField,
  AutoFieldHelpers,
  SetValuesWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import {ScrollableModal} from '../../../components/Common/ScrollableModal';
import validateUUID from '../../../utils/validateUUID';
import {UPDATES} from '../../../shared/UPDATES';

const classes = {
  fieldName: {
    width: '20%',
  },

  wrapper: {
    marginTop: '1rem',
  },
};

export const loadBuilderProtectionPlan = ({
  builderDetails,
  setValues,
}: {
  builderDetails: OvProtectionbuilderDetails_ovbuilderDetails | null;
  setValues: SetValuesWithSourceKey<builderFieldsType>;
}) => {
  if (!builderDetails) {
    return;
  }
  const loadedBuilderProtectionPlan = builderDetails.pots?.[0].pot;
  const protectionPlan: setUpProtectionFields = {
    livesAssured: loadedBuilderProtectionPlan?.livesAssured || undefined,
    premiumType: loadedBuilderProtectionPlan?.premiumType || undefined,
    lifeCover: loadedBuilderProtectionPlan?.lifeCover
      ? String(loadedBuilderProtectionPlan?.lifeCover)
      : undefined,
    protectionType: loadedBuilderProtectionPlan?.protectionType || undefined,

    terminatingAge: loadedBuilderProtectionPlan?.terminatingAge || undefined,
    termType: loadedBuilderProtectionPlan?.termType || undefined,
    criticalIllnessCover: loadedBuilderProtectionPlan?.criticalIllnessCover
      ? String(loadedBuilderProtectionPlan?.criticalIllnessCover)
      : undefined,
    term: toString(loadedBuilderProtectionPlan?.term) || undefined,
    lifeOrCriticalIllnessCover: loadedBuilderProtectionPlan?.lifeOrCriticalIllnessCover
      ? String(loadedBuilderProtectionPlan?.lifeOrCriticalIllnessCover)
      : undefined,
    inTrust: loadedBuilderProtectionPlan?.inTrust || false,
    investmentLinked: loadedBuilderProtectionPlan?.investmentLinked || false,
    monthlyIncome: loadedBuilderProtectionPlan?.monthlyIncome
      ? String(loadedBuilderProtectionPlan?.monthlyIncome)
      : undefined,
    waiverOfPremium: loadedBuilderProtectionPlan?.waiverOfPremium || false,
    notes: loadedBuilderProtectionPlan?.notes || undefined,
    deferredPeriod: loadedBuilderProtectionPlan?.deferredPeriod || undefined,
  };
  setValues(UPDATES._520, (prev) =>
    merge({}, prev, {
      protectionPlan,
      loaded: true,
    })
  );
};

export const numberOnlyTerm = ({
  termObj,
  termHelpers,
}: {
  termObj: FieldInputProps<string | undefined>;
  termHelpers: AutoFieldHelpers<string | undefined>;
}) => {
  // term is only numbers
  const numbersOnly = String(termObj.value).replace(/[^\d.-]+/g, '');
  if (numbersOnly !== termObj.value && numbersOnly !== '') {
    termHelpers.setValue(UPDATES._521, numbersOnly);
  }
};

export const BuilderProtectionPlan: React.FC<BuilderProps> = (props) => {
  const {contactName} = props;
  const [protectionPlanObj, protectionPlanMeta, protectionPlanHelpers] = useAutoField<
    setUpProtectionFields | undefined
  >('protectionPlan');
  const [termObj, termMeta, termHelpers] = useAutoField<
    setUpProtectionFields['term'] | undefined
  >(termField.id);
  const {ovBuilderId} = useBuilderContext();
  const {data, error, loading} = useQuery<
    OvProtectionbuilderDetails,
    OvProtectionbuilderDetailsVariables
  >(OV_PROTECTION_BUILDER_DETAILS, {
    skip: !ovBuilderId,
    variables: {
      ovrecommendId: validateUUID(ovBuilderId)!,
    },
  });
  const builderDetails = useMemo(() => {
    return data?.ovrecommendDetails;
  }, [data?.ovrecommendDetails]);

  const {values, setValues} = useAutoForm<builderFieldsType>();

  useEffect(() => {
    if (builderDetails)
      loadBuilderProtectionPlan({
        builderDetails,
        setValues,
      });
  }, [builderDetails]);
  if (loading) {
    return <Loading entityName="Plans" />;
  }
  return (
    <Box width="100%" mt={3} mb={3}>
      <ProtectionRow contactName={contactName}>
        <Box mt={2}>
          <Grid container direction="row" spacing={2}>
            <Box sx={classes.wrapper} mt={2} mb={2} mr={2} ml={2}>
              <Grid container direction="row" spacing={6}>
                <Grid item xs={6}>
                  <Grid container direction="column" spacing={2}>
                    <Grid item>
                      <AutoField field={protectionTypeField} />
                    </Grid>
                    {values.protectionPlan?.protectionType ? (
                      <BuilderProtectionForm {...props} />
                    ) : null}
                    <Box mt={1} />
                  </Grid>
                </Grid>
                {values.protectionPlan?.protectionType && (
                  <Grid item xs={6}>
                    <Grid container direction="column" spacing={2}>
                      <Grid item>
                        <AutoField field={protectionPlanNotesField} />
                      </Grid>
                      <Box mt={1} />
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid item>
                <Box mt={2} />
                <Typography color="error">
                  <strong>
                    Details of the client&apos;s height, weight and smoker status will
                    need to have been obtained and added to their Client Questionnaire,
                    along with occupation when requiring income protection.
                  </strong>
                </Typography>
              </Grid>
            </Box>
          </Grid>
        </Box>
      </ProtectionRow>
    </Box>
  );
};

export const protectionOrder: (keyof protectionFields)[] = [
  'livesAssured',
  'premiumType',
  'lifeCover',
  'monthlyIncome',
  'criticalIllnessCover',
  'lifeOrCriticalIllnessCover',
  'deferredPeriod',
  'investmentLinked',
  'termType',
  'waiverOfPremium',
  'inTrust',
];

export const BuilderProtectionForm: React.FC<BuilderProps> = ({contactName}) => {
  const {values} = useAutoForm<builderFieldsType>();

  const isSingle =
    values.contact !== OWNERSHIP.JOINT_CODE && values.contact !== OWNERSHIP.JOINT_LIVES;
  const protectionType = values.protectionPlan!.protectionType!;
  const [showOtherProtectionModal, setShowOtherProtectionModal] = useState(false);
  useEffect(() => {
    if (
      values.protectionPlan?.protectionType === ProtectionTypeEnum.other // todo, change to other
    ) {
      setShowOtherProtectionModal(true);
    }
  }, [values.protectionPlan?.protectionType]);
  return (
    <>
      <ScrollableModal size="small" open={showOtherProtectionModal}>
        <Box bgcolor="white" padding={4}>
          <Typography variant="h4">Other Protection Type</Typography>
          <Box mt={2} />
          <Typography variant="body1">
            Please include the Protection Type and any other relevant information not
            covered by the drop down options in the notes section{' '}
          </Typography>
          <Box mt={4} />
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowOtherProtectionModal(false);
            }}
          >
            Ok
          </Button>
        </Box>
      </ScrollableModal>
      {protectionTypeToFieldsMatrix.livesAssured[protectionType] && (
        <Grid item>
          <AutoField
            field={{
              ...livesAssuredField,

              initialValue: isSingle ? contactName : 'both',
              options: isSingle
                ? [{label: contactName, value: contactName}]
                : [
                    {label: 'Both', value: 'both'},
                    {label: 'Both beyond death', value: 'bothBeyondDeath'},
                  ],
            }}
          />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.premiumType[protectionType] && (
        <Grid item>
          <AutoField field={premiumTypeField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.lifeCover[protectionType] && (
        <Grid item>
          <AutoField field={liveCoverField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.monthlyIncome[protectionType] && (
        <Grid item>
          <AutoField field={monthlyIncomeTextField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.criticalIllnessCover[protectionType] && (
        <Grid item>
          <AutoField field={criticalIllnessCoverField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.deferredPeriod[protectionType] && (
        <Grid item>
          <AutoField field={deferredPeriodField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.investmentLinked[protectionType] && (
        <Grid item>
          <AutoField field={investmentLinkedField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.termType[protectionType] && (
        <Grid item>
          <AutoField field={termTypeField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.termType[protectionType] &&
        values.protectionPlan?.termType && (
          <Grid item>
            <AutoField
              field={
                values.protectionPlan?.termType === 'terminatingAge'
                  ? toAgeField
                  : termField
              }
            />
          </Grid>
        )}

      {protectionTypeToFieldsMatrix.waiverOfPremium[protectionType] && (
        <Grid item>
          <AutoField field={waiverOfPremiumField} />
        </Grid>
      )}
      {protectionTypeToFieldsMatrix.inTrust[protectionType] && (
        <Grid item>
          <AutoField field={protectionInTrustField} />
        </Grid>
      )}
    </>
  );
};
